// Admin editor for per-chapter intro content (the about/lesson shown above a
// main-course chapter's conversation list — chapter_content table). Raw HTML
// textareas; same markup contract as the per-conversation editor.

import React, { useCallback, useEffect, useState } from 'react';

import { getChapterContent, putChapterContent } from '../adminApi';

export default function ChapterContent() {
    const [rows, setRows] = useState(null);
    const [error, setError] = useState(null);
    const [newChapter, setNewChapter] = useState('');

    const load = useCallback(async () => {
        try {
            setError(null);
            const data = await getChapterContent();
            setRows(data || []);
        } catch (e) {
            setError(e?.response?.data?.error || e?.message || 'Load failed');
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const addChapter = () => {
        const n = parseInt(newChapter, 10);
        if (!n) return;
        setNewChapter('');
        if ((rows || []).some((r) => r.chapter === n)) return;
        setRows(
            [...(rows || []), { chapter: n, about: '', lesson: '' }].sort(
                (a, b) => a.chapter - b.chapter,
            ),
        );
    };

    if (rows === null) {
        return <div style={styles.page}>Loading…</div>;
    }

    return (
        <div style={styles.page}>
            <h2 style={styles.h2}>Chapter intros</h2>
            <p style={styles.sub}>
                The about/lesson shown above a main-course chapter&apos;s conversation list.
            </p>
            {error ? <div style={styles.err}>{error}</div> : null}

            <div style={styles.addRow}>
                <input
                    style={styles.numInput}
                    type="number"
                    placeholder="Chapter #"
                    value={newChapter}
                    onChange={(e) => setNewChapter(e.target.value)}
                />
                <button type="button" style={styles.btnPrimary} onClick={addChapter}>
                    Add chapter
                </button>
            </div>

            {rows.map((r) => (
                <ChapterCard key={r.chapter} row={r} onSaved={load} />
            ))}
            {rows.length === 0 ? (
                <div style={styles.empty}>No chapter intros yet — add one above.</div>
            ) : null}
        </div>
    );
}

function ChapterCard({ row, onSaved }) {
    const [about, setAbout] = useState(row.about ?? '');
    const [lesson, setLesson] = useState(row.lesson ?? '');
    const [saving, setSaving] = useState(false);
    const [savedTick, setSavedTick] = useState(false);
    const [error, setError] = useState(null);

    const dirty = (about ?? '') !== (row.about ?? '') || (lesson ?? '') !== (row.lesson ?? '');

    const onSave = async () => {
        if (!dirty || saving) return;
        try {
            setSaving(true);
            setError(null);
            await putChapterContent(row.chapter, { about, lesson });
            setSavedTick(true);
            setTimeout(() => setSavedTick(false), 1400);
            onSaved && onSaved();
        } catch (e) {
            setError(e?.response?.data?.error || e?.message || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={styles.card}>
            <h3 style={styles.cardH3}>Chapter {row.chapter}</h3>
            <label style={styles.label}>About (HTML)</label>
            <textarea
                style={styles.textarea}
                value={about}
                onChange={(e) => setAbout(e.target.value)}
                disabled={saving}
                spellCheck={false}
            />
            <label style={styles.label}>Lesson (HTML)</label>
            <textarea
                style={styles.textarea}
                value={lesson}
                onChange={(e) => setLesson(e.target.value)}
                disabled={saving}
                spellCheck={false}
            />
            <div style={styles.actions}>
                {dirty ? (
                    <button type="button" style={styles.btnPrimary} onClick={onSave} disabled={saving}>
                        {saving ? 'Saving…' : 'Save'}
                    </button>
                ) : null}
                {savedTick ? <span style={styles.saved}>Saved ✓</span> : null}
                {error ? <span style={styles.err}>{error}</span> : null}
            </div>
        </div>
    );
}

const styles = {
    page: { maxWidth: 820, color: '#222' },
    h2: { margin: '0 0 4px', fontSize: 22 },
    sub: { margin: '0 0 16px', color: '#877963', fontSize: 13 },
    addRow: { display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16 },
    numInput: { width: 110, padding: '6px 8px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13 },
    card: { backgroundColor: '#fff', borderRadius: 12, padding: 16, marginBottom: 12, boxShadow: '0 1px 2px rgba(0,0,0,0.04)' },
    cardH3: { margin: '0 0 8px', fontSize: 15 },
    label: { display: 'block', fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 0.4, margin: '12px 0 4px' },
    textarea: { width: '100%', minHeight: 140, padding: '8px 10px', borderRadius: 6, border: '1px solid #ddd', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12, lineHeight: 1.5, resize: 'vertical', boxSizing: 'border-box' },
    actions: { display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 },
    btnPrimary: { padding: '6px 12px', borderRadius: 6, border: '1px solid #d97447', background: '#d97447', color: '#fff', fontWeight: 600, cursor: 'pointer' },
    saved: { color: '#1FA265', fontSize: 12 },
    err: { color: '#c0392b', fontSize: 12 },
    empty: { color: '#877963', fontSize: 13 },
};
